import { type ReactNode } from 'react';
import { Frown } from 'lucide-react';
import { useSupportLink } from '@/hooks/use-support-link';
import type { AuthUser } from '@/types/auth';
import {
  getBanReason,
  PendingAmount,
  WalletCta,
  type PendingSummary,
} from './banned-dashboard-parts';

type Props = { user: AuthUser; summary: PendingSummary | undefined };

export function isEligible(summary: PendingSummary | undefined): boolean {
  if (!summary) return false;
  const min = summary.minimum_payout_banned ?? 3;
  return summary.accumulated >= min;
}

export function BannedHeader({ user }: { user: AuthUser }): ReactNode {
  return (
    <div className="flex flex-col items-center space-y-sm text-center">
      <Frown className="h-10 w-10 text-text-tertiary" strokeWidth={1.5} />
      <h1 className="text-xl font-bold text-text-primary">Game over</h1>
      <p className="text-sm text-text-tertiary">{getBanReason(user)}</p>
    </div>
  );
}

function PendingBody({ user, summary }: Props): ReactNode {
  if (!summary) return null;
  const hasWallet = !!user.usdc_wallet?.trim();
  return (
    <div className="space-y-sm">
      <PendingAmount summary={summary} />
      {hasWallet ? (
        <p className="text-sm text-text-secondary">
          Tu pago se procesara automaticamente.
        </p>
      ) : (
        <WalletCta summary={summary} />
      )}
    </div>
  );
}

export function PendingCard({ user, summary }: Props): ReactNode {
  if (!isEligible(summary)) return null;
  return (
    <div className="space-y-sm rounded-lg border border-border-subtle p-base">
      <h2 className="text-sm font-medium text-text-primary">Pago pendiente</h2>
      <PendingBody user={user} summary={summary} />
    </div>
  );
}

export function ContactFooter(): ReactNode {
  const link = useSupportLink();
  return (
    <p className="text-center text-xs text-text-tertiary">
      ¿Alguna pregunta? Escríbenos a{' '}
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand-red hover:text-brand-coral"
        >
          soporte
        </a>
      ) : (
        <span className="font-medium text-text-secondary">soporte</span>
      )}
    </p>
  );
}
